import type { TreeSnapshot } from '@lineage/core';
import { auth } from './firebase';
import { translateMessage } from '../i18n';
import { consentHeader } from './analytics';

const ORIGIN = (import.meta.env.VITE_API_ORIGIN as string | undefined) ?? '';

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
  ) {
    super(message);
  }
}

export async function api<T = unknown>(method: string, path: string, body?: unknown): Promise<T> {
  const headers: Record<string, string> = { ...consentHeader() };
  const token = await auth.currentUser?.getIdToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  let payload: BodyInit | undefined;
  if (body instanceof FormData) payload = body;
  else if (body !== undefined) {
    headers['Content-Type'] = 'application/json';
    payload = JSON.stringify(body);
  }
  const res = await fetch(`${ORIGIN}/api${path}`, { method, headers, body: payload });
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  const json = text ? (JSON.parse(text) as unknown) : null;
  if (!res.ok) {
    const e = (json as { error?: { code?: string; message?: string } } | null)?.error;
    const code = e?.code ?? 'error';
    throw new ApiError(res.status, code, e?.message ? translateMessage(e.message, code) : `Request failed (${res.status})`);
  }
  return json as T;
}

export const photoUrl = (id: string, size: 'full' | '256') => `${ORIGIN}/api/photos/${id}/${size}`;

export interface MeTree {
  id: string;
  name: string;
  role: 'owner' | 'editor' | 'viewer';
  personId: string | null;
}
export interface Me {
  user: {
    id: string;
    email: string | null;
    emailVerified: boolean;
    displayName: string | null;
    /** Relationship words chosen on the account; null = automatic. Missing from older API versions. */
    kinPack?: string | null;
    /** App language chosen on the account; null = automatic. */
    locale?: string | null;
    /** Terms version last accepted; null = never. Missing from older API versions. */
    termsVersion?: string | null;
    /** Analytics choice on the account; null = not asked. */
    analyticsConsent?: 'granted' | 'denied' | null;
  };
  trees: MeTree[];
}
export interface TreeListItem extends MeTree {
  memberCount: number;
  peopleCount: number;
  version: number;
}
export type Snapshot = TreeSnapshot;
export interface Member {
  userId: string;
  email: string | null;
  displayName: string | null;
  role: 'owner' | 'editor' | 'viewer';
  personId: string | null;
  joinedAt: number;
}
export interface Claim {
  id: string;
  userId: string;
  personId: string;
  email: string | null;
  displayName: string | null;
}
export interface InvitePreview {
  treeName: string;
  inviter: string;
  role: 'editor' | 'viewer';
  emailBound: boolean;
  email: string | null;
  person: { name: string; hasPortrait: boolean } | null;
}

/** Authenticated binary fetch (images can't carry a Bearer header, so we fetch and use blob URLs). */
export async function apiBlob(path: string): Promise<Blob> {
  const token = await auth.currentUser?.getIdToken();
  const res = await fetch(`${ORIGIN}/api${path}`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
  if (!res.ok) throw new ApiError(res.status, 'error', 'Image unavailable');
  return res.blob();
}
